import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { styled } from '@linaria/react';
import { InputText } from '@daedalus/atlas/experimental/InputText';
import { InputTextType } from '@daedalus/atlas/helpers/InputBase';
import { Icon } from '@daedalus/atlas/Icon';
import useDebounce from '@daedalus/core/src/utils/debounce/hooks/useDebounce';
import { IconButton } from '../IconButton';
import { destinationPickerMessages } from './SearchFormHorizontal';
import { IconWrapper } from './styles';
interface Props {
  /** Destination name */
  destination: string;
  /** Callback for when autocomplete data is requested */
  onSuggestionsRequested: (value: string, shouldLoadUserSearchHistory: boolean) => void;
  /** Whether search history should be shown */
  shouldLoadUserSearchHistory?: boolean;
  /** Whether the input should be focused on mount */
  focusOnMount?: boolean;
}
const _exp = /*#__PURE__*/() => InputText;
const DestinationInput = /*#__PURE__*/styled(_exp())({
  name: "DestinationInput",
  class: "ds3kiv9",
  propsAsIs: true
});
export const SearchFormDestinationInput = React.memo(function SearchFormDestinationInput({
  destination,
  onSuggestionsRequested,
  shouldLoadUserSearchHistory = false,
  focusOnMount = false
}: Props) {
  const {
    formatMessage
  } = useIntl();
  const inputFieldRef = useRef<null | HTMLInputElement>(null);
  const [inputValue, setInputValue] = useState(destination);
  const debouncedInputValue = useDebounce(inputValue, 250);
  useEffect(() => {
    if (focusOnMount) inputFieldRef.current?.focus?.();
  }, [focusOnMount]);
  useEffect(() => {
    setInputValue(destination);
  }, [setInputValue, destination]);

  // Requesting suggestions can cause a loop when we rely on dep stability so check that value is actually changed
  const previousDebouncedInputValue = useRef<string>(debouncedInputValue);
  useEffect(() => {
    const isChanged = debouncedInputValue !== previousDebouncedInputValue.current;
    if (!isChanged) return;
    previousDebouncedInputValue.current = debouncedInputValue;
    onSuggestionsRequested(debouncedInputValue ?? '', Boolean(!debouncedInputValue));
  }, [debouncedInputValue, onSuggestionsRequested]);
  const handleInputChange = useCallback((value: string) => {
    setInputValue(value);
  }, [setInputValue]);
  const handleInputFocus = useCallback(({
    target
  }: React.FocusEvent<HTMLInputElement>) => {
    const {
      value
    } = target;
    if (destination === '') {
      onSuggestionsRequested('', true);
    } else {
      onSuggestionsRequested(value, shouldLoadUserSearchHistory);
    }
  }, [destination, shouldLoadUserSearchHistory, onSuggestionsRequested]);
  const handleClearDestination = useCallback((event: React.SyntheticEvent) => {
    event.preventDefault();
    setInputValue('');
    inputFieldRef.current?.focus();
  }, [inputFieldRef, setInputValue]);
  return <DestinationInput ref={inputFieldRef} customProps={{
    role: 'presentation',
    autoCapitalize: 'off',
    autoCorrect: 'off',
    spellCheck: 'false',
    autocomplete: 'off',
    dataLpignore: true,
    dataFormType: 'other'
  }} id="DestinationInput" dataId="DestinationInput" name="DestinationInput" onFocus={handleInputFocus} prefix={<IconWrapper>
            <Icon name="Pin" />
          </IconWrapper>} placeholder={formatMessage(destinationPickerMessages.destinationPlaceholder)} type={InputTextType.search} value={inputValue} onChange={handleInputChange} suffix={inputValue && <IconButton onClick={handleClearDestination} variant="quiet" size="sm" dataId="DestinationInputClear">
              <Icon name="Clear" colorPath="content.neutral.c600" />
            </IconButton>} />;
});

require("./SearchFormDestinationInput.wyw-in-js.css!=!../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./SearchFormDestinationInput.tsx");